import { observer } from "mobx-react-lite";
import React from "react";
import { XStack, YStack } from "tamagui";
import { ModuleType } from "../../../../templates/types";
import { Body } from "../../../ui/Body";
import { Box } from "../../../ui/Box";
import { Title } from "../../../ui/Title";
import { CreateWizardButton } from "./CreateWizardButton";
import { CreateWizardProgress } from "./CreateWizardProgress";
import {
  type CreateWizardModule,
  type CreateWizardState,
  CreateWizardStep,
} from "./CreateWizardState";
import { Selection } from "./Selection";

type Module = {
  data: CreateWizardModule;
  title: string; // Displayed when selecting what cards the user wants
  description: string; // Displayed when selecting what cards the user wants. Should be short 1-2, sentences
};

const wizardModules: Module[] = [
  {
    data: {
      id: "budget-planning",
      title: "Budget",
      emoji: "💰",
      type: ModuleType.Poll,
      multipleVotes: true,
    },
    title: "Budget",
    description: "Agree on the total cost for the event.",
  },
  {
    data: {
      id: "split-expenses",
      title: "Expenses",
      emoji: "🧾",
      type: ModuleType.Expense,
    },
    title: "Track group expenses",
    description: "Record costs and settle debts with ease.",
  },
  {
    data: {
      id: "poll-activities",
      title: "Activities",
      emoji: "🍿",
      type: ModuleType.Poll,
      multipleVotes: true,
    },
    title: "Vote on activities",
    description: "Let participants vote on activities.",
  },
  {
    data: {
      id: "poll-food",
      title: "Food",
      emoji: "🍽️",
      type: ModuleType.Poll,
      multipleVotes: true,
    },
    title: "Vote on food options",
    description: "Let participants vote on food preferences or menus.",
  },
  {
    data: {
      id: "poll-drinks",
      title: "Drinks",
      emoji: "🍹",
      type: ModuleType.Poll,
      multipleVotes: true,
    },
    title: "Vote on drinks",
    description: "Get everyone's input on drink choices for the event.",
  },
  {
    data: {
      id: "shopping-list",
      title: "Shopping List",
      emoji: "🛒",
      type: ModuleType.Checkboxes,
    },
    title: "Organize shopping items",
    description: "Create and check off items for the group to bring or buy.",
  },
  {
    data: {
      id: "tasks",
      title: "Tasks",
      emoji: "✅",
      type: ModuleType.Checkboxes,
    },
    title: "Assign and track tasks",
    description: "Organize responsibilities and track progress.",
  },
  {
    data: {
      id: "event-info",
      title: "Information",
      emoji: "ℹ️",
      type: ModuleType.Info,
    },
    title: "Provide event details",
    description: "Share key information about the event in one place.",
  },
  {
    data: {
      id: "rsvp",
      title: "RSVP",
      emoji: "📩",
      type: ModuleType.Rsvp,
    },
    title: "Collect RSVPs",
    description: "Confirm attendance and gather responses from participants.",
  },
];

export const WizardStepModules: React.FC<{ state: CreateWizardState }> =
  observer((props) => {
    const hasModulesAdded = props.state.modules.size > 0;

    const modulesList = Array.from(props.state.modules.values())
      .map((module) => module.title)
      .join(", ");

    return (
      <>
        <YStack gap="$medium">
          <Box backgroundColor={"$primary"} gap={"$small"}>
            <Title>
              Almost done, do you want to Planira to help you with something
              else?
            </Title>
            <Body>
              Select the steps that you want Planira to help you with to create
              the best experience for you and your group
            </Body>

            <CreateWizardProgress state={props.state} />
          </Box>

          <XStack flexWrap="wrap" gap={"$medium"}>
            {wizardModules.map((module) => (
              <Selection
                showChevron={false}
                selected={props.state.modules.has(module.data.id)}
                key={module.data.id}
                icon={module.data.emoji}
                title={module.title}
                description={module.description}
                onSelect={() => {
                  props.state.toggleModule(module.data);
                }}
              />
            ))}
          </XStack>
        </YStack>
        <CreateWizardButton
          type={hasModulesAdded ? "primary" : "secondary"}
          onPress={() => props.state.setActiveStepId(CreateWizardStep.INFO)}
          position={"sticky" as "static"}
          bottom={0}
          subText={
            hasModulesAdded ? modulesList : "You can add more steps later on"
          }
        >
          {hasModulesAdded ? "Continue" : "Skip for now"}
        </CreateWizardButton>
      </>
    );
  });
